import type { Language } from '../types/document'

export interface LanguageInfo {
  code: Language
  /** Name in its own script, shown in the picker. */
  nativeName: string
  englishName: string
  /** BCP-47 tag used for speech voices and date formatting. */
  bcp47: string
  /** Tesseract model that reads this language's script. */
  ocrModel: string
  /** IndicTrans2 target code (null for English). */
  indicTrans: string | null
  /** Built-in MMS voice folder under /voices/ (null: rely on the device's voice). */
  voice: string | null
}

export const LANGUAGES: LanguageInfo[] = [
  { code: 'hi', nativeName: 'हिंदी', englishName: 'Hindi', bcp47: 'hi-IN', ocrModel: 'hin', indicTrans: 'hin_Deva', voice: 'hin' },
  { code: 'en', nativeName: 'English', englishName: 'English', bcp47: 'en-IN', ocrModel: 'eng', indicTrans: null, voice: null },
  { code: 'bn', nativeName: 'বাংলা', englishName: 'Bengali', bcp47: 'bn-IN', ocrModel: 'ben', indicTrans: 'ben_Beng', voice: 'ben' },
  { code: 'mr', nativeName: 'मराठी', englishName: 'Marathi', bcp47: 'mr-IN', ocrModel: 'mar', indicTrans: 'mar_Deva', voice: 'mar' },
  { code: 'ta', nativeName: 'தமிழ்', englishName: 'Tamil', bcp47: 'ta-IN', ocrModel: 'tam', indicTrans: 'tam_Taml', voice: 'tam' },
  { code: 'te', nativeName: 'తెలుగు', englishName: 'Telugu', bcp47: 'te-IN', ocrModel: 'tel', indicTrans: 'tel_Telu', voice: 'tel' },
  { code: 'gu', nativeName: 'ગુજરાતી', englishName: 'Gujarati', bcp47: 'gu-IN', ocrModel: 'guj', indicTrans: 'guj_Gujr', voice: 'guj' },
  { code: 'kn', nativeName: 'ಕನ್ನಡ', englishName: 'Kannada', bcp47: 'kn-IN', ocrModel: 'kan', indicTrans: 'kan_Knda', voice: 'kan' },
  { code: 'ml', nativeName: 'മലയാളം', englishName: 'Malayalam', bcp47: 'ml-IN', ocrModel: 'mal', indicTrans: 'mal_Mlym', voice: 'mal' },
  { code: 'pa', nativeName: 'ਪੰਜਾਬੀ', englishName: 'Punjabi', bcp47: 'pa-IN', ocrModel: 'pan', indicTrans: 'pan_Guru', voice: 'pan' },
  { code: 'or', nativeName: 'ଓଡ଼ିଆ', englishName: 'Odia', bcp47: 'or-IN', ocrModel: 'ori', indicTrans: 'ory_Orya', voice: 'ory' },
  { code: 'as', nativeName: 'অসমীয়া', englishName: 'Assamese', bcp47: 'as-IN', ocrModel: 'asm', indicTrans: 'asm_Beng', voice: 'asm' }
]

export const LANGUAGE_CODES = LANGUAGES.map((l) => l.code)

export function languageInfo(code: Language): LanguageInfo {
  return LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0]
}

export function isSupportedLanguage(code: unknown): code is Language {
  return typeof code === 'string' && (LANGUAGE_CODES as string[]).includes(code)
}

/**
 * Which Tesseract models to load for a document. English is always included
 * because most Indian government papers mix English with a regional script.
 */
export function ocrModelsFor(docLanguage: Language): string[] {
  const model = languageInfo(docLanguage).ocrModel
  return model === 'eng' ? ['eng', 'hin'] : ['eng', model]
}
